import { Head, Link } from "@inertiajs/react"
import { Check, ChevronRight, ChevronLeft } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { WeeklyReportsSubNav } from "@/components/weekly_reports/WeeklyReportsSubNav"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import type {
  GridDepartment,
  GridPaging,
  GridPerson,
  GridWeek,
} from "@/types/weekly_report"

const DESCRIPTION =
  "Who has filed a weekly activity report and who has not, department by department, across the last eight weeks."

export default function WeeklyReportsGrid({
  weeks,
  departments,
  filed_counts,
  people_count,
  paging,
}: {
  weeks: GridWeek[]
  departments: GridDepartment[]
  filed_counts: number[]
  people_count: number
  paging: GridPaging
}) {
  const columns = weeks.length + 1

  return (
    <>
      <Head title="Weekly Reports">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Weekly Reports" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <PageHeader
          title="Weekly Reports"
          description={`Filed and missing across the last ${weeks.length} weeks, for ${people_count} ${
            people_count === 1 ? "person" : "people"
          }.`}
          tabs={<WeeklyReportsSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ink-muted">
              {weeks[weeks.length - 1]?.range} — {weeks[0]?.range}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="secondary" asChild>
                <Link href={`/weekly_reports?week_ending=${paging.earlier}`}>
                  <ChevronLeft className="size-4" aria-hidden="true" />
                  Earlier
                </Link>
              </Button>
              {paging.later ? (
                <Button variant="secondary" asChild>
                  <Link href={`/weekly_reports?week_ending=${paging.later}`}>
                    Later
                    <ChevronRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>
              ) : null}
              {paging.current ? (
                <Button variant="ghost" asChild>
                  <Link href="/weekly_reports">This week</Link>
                </Button>
              ) : null}
            </div>
          </div>

          {/*
            The table renders its own horizontal scroller, so the grid stays
            inside its box however many weeks are on show and the page body
            never scrolls sideways. The person column is `sticky`, so scrolling
            the weeks does not leave a row of anonymous marks.
          */}
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell sticky>Person</TableHeaderCell>
                {weeks.map((week, index) => (
                  <TableHeaderCell key={week.value} className="text-center">
                    <span className="block">Wk {week.number}</span>
                    <span className="block font-normal text-ink-muted">
                      {week.range.replace(/, \d{4}$/, "")}
                    </span>
                    <span className="block font-normal text-ink-muted">
                      {filed_counts[index]}/{people_count}
                    </span>
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHead>

            {departments.map((department) => (
              <TableBody key={department.name}>
                <TableRow className="bg-surface">
                  {/*
                    The label is sticky, not the cell. A cell spanning every
                    column fills its row exactly, and `position: sticky` can
                    never move an element outside its containing block — so a
                    sticky cell here has no slack and the department name simply
                    scrolls away. A sticky span inside the wide cell has room.
                    `left-3` matches the cell's own `px-3`.
                  */}
                  <TableCell colSpan={columns} className="py-3">
                    <span className="sticky left-3 inline-block">
                      {/*
                        A step up from the table's own text-xs, and the heaviest
                        weight in the grid. text-ink-display is already the
                        darkest ink token, so weight and size are what separate a
                        group heading from the rows under it. The tally stays
                        small and muted so the department name carries the row.
                      */}
                      <span className="text-sm font-semibold text-ink-display">
                        {department.name}
                      </span>
                      <span className="ml-2 text-ink-muted">
                        {department.filed}/{department.expected} filed
                      </span>
                    </span>
                  </TableCell>
                </TableRow>
                {department.people.map((person) => (
                  <PersonRow key={person.id} person={person} weeks={weeks} />
                ))}
              </TableBody>
            ))}
          </Table>

          <p className="mt-3 text-sm text-ink-muted">
            A tick opens that week&apos;s report. Reports brought over from the old
            system sit alongside the ones filed here.
          </p>
        </div>
      </AppShell>
    </>
  )
}

function PersonRow({ person, weeks }: { person: GridPerson; weeks: GridWeek[] }) {
  return (
    <TableRow>
      <TableCell sticky nowrap>
        {person.recent.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger className="cursor-pointer text-left underline decoration-hairline underline-offset-2 hover:text-ink-display">
              {person.name}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Last {person.recent.length} reports</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {person.recent.map((report) => (
                <DropdownMenuItem key={report.id} asChild>
                  <Link href={`/weekly_reports/${report.id}`}>{report.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span>{person.name}</span>
        )}
      </TableCell>

      {person.cells.map((cell, index) => (
        <TableCell key={weeks[index].value} className="text-center">
          {cell ? (
            <Link
              href={`/weekly_reports/${cell.report_id}`}
              className="inline-flex items-center justify-center gap-1 no-underline"
              aria-label={`${person.name} filed ${cell.week_label}`}
            >
              <Check className="size-4 text-accent" aria-hidden="true" />
              {cell.count > 1 && (
                <span className="text-ink-muted">{cell.count}</span>
              )}
            </Link>
          ) : (
            <span className="text-ink-muted">
              <span aria-hidden="true">—</span>
              <span className="sr-only">
                {person.name} did not file {weeks[index].label}
              </span>
            </span>
          )}
        </TableCell>
      ))}
    </TableRow>
  )
}
